import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, PanInfo, useMotionValue, useTransform } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ImageCarouselProps {
  images: string[];
  className?: string;
  showControls?: boolean;
  showDots?: boolean;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  onImageChange?: (index: number) => void;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  className,
  showControls = true,
  showDots = true,
  autoPlay = false,
  autoPlayInterval = 5000,
  onImageChange,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dragConstraints, setDragConstraints] = useState({ left: 0, right: 0 });
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const x = useMotionValue(0);
  const opacity = useTransform(x, [-100, 0, 100], [0.5, 1, 0.5]);

  const imageWidth = 100; // Percentage
  const swipeThreshold = 50;

  // Load images to trigger imagesLoaded state
  useEffect(() => {
    if (!images || images.length === 0) return;

    const loadImages = async () => {
      await Promise.all(
        images.map((src) => {
          return new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve(); // Continue even if image fails
            img.src = src;
          });
        })
      );
      setImagesLoaded(true);
    };

    loadImages();
  }, [images]);

  // Update drag constraints when images change
  useEffect(() => {
    if (images.length > 1) {
      const maxDrag = (images.length - 1) * imageWidth;
      setDragConstraints({ left: -maxDrag, right: 0 });
    }
  }, [images.length]);

  // Auto-play functionality
  useEffect(() => {
    if (!autoPlay || images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [autoPlay, autoPlayInterval, images.length]);

  // Notify parent of image changes
  useEffect(() => {
    onImageChange?.(currentIndex);
  }, [currentIndex, onImageChange]);

  const handleDragEnd = useCallback(
    (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      const dragDistance = info.offset.x;
      const velocity = info.velocity.x;

      // Determine direction based on drag distance and velocity
      if (Math.abs(dragDistance) > swipeThreshold || Math.abs(velocity) > 500) {
        if (dragDistance > 0 && currentIndex > 0) {
          // Swipe right - previous image
          setCurrentIndex(currentIndex - 1);
        } else if (dragDistance < 0 && currentIndex < images.length - 1) {
          // Swipe left - next image
          setCurrentIndex(currentIndex + 1);
        }
      }

      // Reset motion value with spring animation
      x.set(0);
    },
    [currentIndex, images.length, x]
  );

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  const goToNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, images.length]);

  const goToImage = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  if (!images || images.length === 0) {
    return null;
  }

  if (images.length === 1) {
    return (
      <div className={cn("relative w-full", className)}>
        <div className="bg-muted overflow-hidden max-h-[500px] md:max-h-[600px] lg:max-h-[700px] flex items-center">
          <img
            src={images[0]}
            alt="Image"
            className="w-full h-auto object-contain"
            loading="lazy"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={cn("relative w-full", className)}>
      <div className="bg-muted overflow-hidden relative max-h-[500px] md:max-h-[600px] lg:max-h-[700px] flex items-center">
        <motion.div
          className="flex h-full"
          animate={{ x: `-${currentIndex * 100}%` }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
            mass: 0.8,
          }}
          drag="x"
          dragConstraints={{ left: -100, right: 100 }}
          dragElastic={0.1}
          onDragEnd={handleDragEnd}
          style={{ x, opacity }}
        >
          <AnimatePresence mode="wait">
            {images.map((image, index) => (
              <motion.div
                key={`${image}-${index}`}
                className="min-w-full h-full relative"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src={image}
                  alt={`Image ${index + 1}`}
                  className="w-full h-full object-cover select-none"
                  loading={index === currentIndex ? "eager" : "lazy"}
                  draggable={false}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Navigation Controls */}
        {showControls && images.length > 1 && (
          <>
            <motion.button
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/30 text-white hover:bg-black/50 transition-colors z-10"
              onClick={goToPrevious}
              disabled={currentIndex === 0}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: currentIndex > 0 ? 1 : 0.3 }}
            >
              <ChevronLeft className="h-5 w-5" />
            </motion.button>

            <motion.button
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/30 text-white hover:bg-black/50 transition-colors z-10"
              onClick={goToNext}
              disabled={currentIndex === images.length - 1}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: currentIndex < images.length - 1 ? 1 : 0.3 }}
            >
              <ChevronRight className="h-5 w-5" />
            </motion.button>
          </>
        )}

        {/* Dot Indicators */}
        {showDots && images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
            {images.map((_, index) => (
              <motion.button
                key={index}
                className={cn(
                  "w-2 h-2 rounded-full transition-colors",
                  index === currentIndex
                    ? "bg-white"
                    : "bg-white/50 hover:bg-white/70"
                )}
                onClick={() => goToImage(index)}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.8 }}
                animate={{
                  scale: index === currentIndex ? 1.2 : 1,
                  opacity: index === currentIndex ? 1 : 0.7,
                }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                }}
              />
            ))}
          </div>
        )}

        {/* Image Counter */}
        {images.length > 1 && (
          <motion.div
            className="absolute top-3 right-3 px-3 py-1 bg-black/50 text-white text-sm rounded-full z-10"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {currentIndex + 1} / {images.length}
          </motion.div>
        )}
      </div>

      {/* Touch indicator */}
      {images.length > 1 && (
        <motion.div
          className="absolute inset-0 pointer-events-none flex items-center justify-center"
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ delay: 2, duration: 1 }}
        >
          <div className="bg-black/20 text-white px-3 py-1 rounded-full text-xs">
            Swipe to navigate
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ImageCarousel;